import { BaseTenantSearch } from '@/services/_base/BaseTenantSearch.ts'
import { type Organization } from '@/types/Organization.ts'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class OrganizationSearch extends BaseTenantSearch<Organization> {
  constructor(session: Session, firestore: IFirestoreService) {
    super('organizations', session, 'organization.search', firestore)
  }

  /** Filter to organizations with a non-empty email */
  withEmail() {
    // Range filter excludes empty strings and null/undefined
    this.where('email', '>=', ' ').where('email', '<=', '\uf8ff')
    this.appendToMetaName('withEmail')
    return this
  }

  /** Prefix search on precomputed lowercased `name_lower` */
  prefixName(termRaw: string, limit = 25) {
    const t = termRaw.trim().toLowerCase()
    const end = `${t}\uf8ff`
    this.where('name_lower', '>=', t)
      .where('name_lower', '<=', end)
      .orderBy('name_lower', 'asc')
      .limit(limit)
    this.appendToMetaName('prefix')
    return this
  }

  /** Find by exact name match using normalized fields */
  byExactName(name: string) {
    this.where('name_lower', '==', name.toLowerCase())
    this.appendToMetaName('byExactName')
    return this
  }

  /** Find by organization type */
  byType(type: string) {
    this.where('type', '==', type)
    this.appendToMetaName('byType')
    return this
  }

  /** Find by organization status */
  byStatus(status: string) {
    this.where('status', '==', status)
    this.appendToMetaName('byStatus')
    return this
  }

  /** Find by engagement stage */
  byEngagementStage(stage: string) {
    this.where('engagementStage', '==', stage)
    this.appendToMetaName('byEngagementStage')
    return this
  }

  /** Find by industry */
  byIndustry(industry: string) {
    this.where('industry', '==', industry)
    this.appendToMetaName('byIndustry')
    return this
  }

  /** Find by city */
  byCity(city: string) {
    this.where('city', '==', city)
    this.appendToMetaName('byCity')
    return this
  }

  /** Find by state */
  byState(state: string) {
    this.where('state', '==', state)
    this.appendToMetaName('byState')
    return this
  }

  /** Find by assigned user */
  byAssignedTo(userId: string) {
    this.where('assignedTo', '==', userId)
    this.appendToMetaName('byAssignedTo')
    return this
  }

  /** Find by tag */
  byTag(tag: string) {
    this.where('tags', 'array-contains', tag)
    this.appendToMetaName('byTag')
    return this
  }

  /** Find by tax ID */
  byTaxId(taxId: string) {
    this.where('taxId', '==', taxId)
    this.appendToMetaName('byTaxId')
    return this
  }

  /** Find by email domain */
  byEmailDomain(domain: string) {
    this.where('email', '>=', `@${domain}`).where('email', '<=', `@${domain}\uf8ff`)
    this.appendToMetaName('byEmailDomain')
    return this
  }

  /** Find organizations that need follow-up */
  needingFollowUp(daysCutoff = 30) {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysCutoff)
    this.where('lastContactedAt', '<', cutoffDate).where('isDeleted', '==', false)
    this.appendToMetaName('needingFollowUp')
    return this
  }

  /** Find organizations with upcoming follow-up dates */
  withUpcomingFollowUp() {
    const now = new Date()
    this.where('nextFollowUpDate', '<=', now).where('isDeleted', '==', false)
    this.appendToMetaName('upcomingFollowUp')
    return this
  }

  /** Find organizations by sales range */
  bySalesRange(minSales?: number, maxSales?: number) {
    if (minSales !== undefined) {
      this.where('sales', '>=', minSales)
    }
    if (maxSales !== undefined) {
      this.where('sales', '<=', maxSales)
    }
    this.appendToMetaName('bySalesRange')
    return this
  }

  /** Find organizations by employee count range */
  byEmployeeRange(minEmployees?: number, maxEmployees?: number) {
    if (minEmployees !== undefined) {
      this.where('employees', '>=', minEmployees)
    }
    if (maxEmployees !== undefined) {
      this.where('employees', '<=', maxEmployees)
    }
    this.appendToMetaName('byEmployeeRange')
    return this
  }

  /** Get recently updated organizations */
  recentlyUpdated(limit = 50) {
    // Ensure updatedAt is the only order so required index matches expectations
    this.spec.orders = []
    this.orderBy('updatedAt', 'desc')
    this.limit(limit)
    this.appendToMetaName('recent')
    return this
  }

  /** Count organizations by type */
  countByType(type: string) {
    this.where('type', '==', type)
    this.appendToMetaName('countByType')
    return this
  }

  /** Count organizations by status */
  countByStatus(status: string) {
    this.where('status', '==', status)
    this.appendToMetaName('countByStatus')
    return this
  }

  /** Count organizations by engagement stage */
  countByEngagementStage(stage: string) {
    this.where('engagementStage', '==', stage)
    this.appendToMetaName('countByEngagementStage')
    return this
  }

  /** Filter by customer type */
  byCustomerType() {
    this.where('type', '==', 'customer')
    this.appendToMetaName('byCustomerType')
    return this
  }

  /** Filter by supplier type */
  bySupplierType() {
    this.where('type', '==', 'supplier')
    this.appendToMetaName('bySupplierType')
    return this
  }

  /** Filter by both customer and supplier type */
  byBothType() {
    this.where('type', '==', 'both')
    this.appendToMetaName('byBothType')
    return this
  }

  /** Search organizations by name, email, or phone (text search) */
  byText(query: string) {
    const q = query.toLowerCase()
    // This would need to be implemented with a more sophisticated search
    // For now, we'll search by name prefix
    this.where('name_lower', '>=', q).where('name_lower', '<=', `${q}\uf8ff`)
    this.appendToMetaName('byText')
    return this
  }

  /** Get all organizations (no filters) */
  all() {
    this.where('isDeleted', '==', false)
    this.appendToMetaName('all')
    return this
  }

  /** Optional: quick paging helpers if/when you add cursors to the spec */
  startAfter(...values: unknown[]) {
    this.cursor('startAfter', ...values)
    return this
  }
}
